import { useEffect, useMemo, useState } from "react";

const FALLBACK_CURRENCY = "USD";

export const CURRENCY_STORAGE_KEY = "currency_code";
export const CURRENCY_UPDATED_EVENT = "currency_code_updated";

export const readStoredCurrencyCode = (): string | null => {
  try {
    return localStorage.getItem(CURRENCY_STORAGE_KEY) || null;
  } catch {
    return null;
  }
};

export const storeCurrencyCode = (code: string) => {
  try {
    if (localStorage.getItem(CURRENCY_STORAGE_KEY) === code) return;
    localStorage.setItem(CURRENCY_STORAGE_KEY, code);
  } catch {
    return;
  }
  window.dispatchEvent(new CustomEvent(CURRENCY_UPDATED_EVENT, { detail: code }));
};

export const useStoredCurrencyCode = (refreshKey?: unknown) => {
  const [currencyCode, setCurrencyCode] = useState<string | null>(readStoredCurrencyCode);

  useEffect(() => {
    const refresh = () => setCurrencyCode(readStoredCurrencyCode());
    const handleStorage = (event: StorageEvent) => {
      if (event.key === CURRENCY_STORAGE_KEY) refresh();
    };
    refresh();
    window.addEventListener(CURRENCY_UPDATED_EVENT, refresh);
    window.addEventListener("storage", handleStorage);
    window.addEventListener("focus", refresh);
    return () => {
      window.removeEventListener(CURRENCY_UPDATED_EVENT, refresh);
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("focus", refresh);
    };
  }, [refreshKey]);

  return currencyCode;
};

const CURRENCY_ICON_SUFFIX: Record<string, string> = {
  USD: "dollar",
  NZD: "dollar",
  HKD: "dollar",
  CAD: "dollar-canadian",
  AUD: "dollar-australian",
  SGD: "dollar-singapore",
  EUR: "euro",
  GBP: "pound",
  EGP: "pound",
  JPY: "yen",
  CNY: "yuan",
  CHF: "frank",
  XOF: "frank",
  XAF: "frank",
  SEK: "krone-swedish",
  CZK: "krone-czech",
  MXN: "peso",
  CLP: "peso",
  PHP: "peso",
  COP: "peso",
  UYU: "peso",
  TRY: "lira",
  RUB: "rubel",
  INR: "rupee",
  LKR: "rupee",
  PKR: "rupee",
  KRW: "won",
  BRL: "real",
  PLN: "zloty",
  HUF: "forint",
  ILS: "shekel",
  AED: "dirham",
  MAD: "dirham",
  SAR: "riyal",
  QAR: "riyal",
  RON: "leu",
  THB: "baht",
  VND: "dong",
  BDT: "taka",
  NGN: "naira",
  TND: "dinar",
};

const COIN_ICON_SUFFIX: Record<string, string> = {
  EUR: "euro",
  GBP: "pound",
  JPY: "yen",
  CNY: "yuan",
  INR: "rupee",
  BDT: "taka",
};

export interface MoneyFormat {
  code: string;
  symbol: string;
  icon: string;
  coinIcon: string;
  filterIcon: string;
  compact: (value: number) => string;
  amount: (value: number) => string;
  whole: (value: number) => string;
  short: (value: number) => string;
}

const finite = (value: number) => (Number.isFinite(value) ? value : 0);

const trimDecimals = (value: number, digits: number) => String(Number(finite(value).toFixed(digits)));

const buildFormatter = (code: string, options: Intl.NumberFormatOptions) => {
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: code, ...options });
  } catch {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: FALLBACK_CURRENCY, ...options });
  }
};

const isValidCurrency = (code: string) => {
  try {
    new Intl.NumberFormat("en-US", { style: "currency", currency: code });
    return true;
  } catch {
    return false;
  }
};

export const createMoneyFormat = (currencyCode?: string | null): MoneyFormat => {
  const requested = String(currencyCode || "").trim().toUpperCase();
  const code = requested && isValidCurrency(requested) ? requested : FALLBACK_CURRENCY;

  const compactFormatter = buildFormatter(code, { notation: "compact", maximumFractionDigits: 2 });
  const amountFormatter = buildFormatter(code, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const wholeFormatter = buildFormatter(code, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  const plainFormatter = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 });

  const symbol = amountFormatter.formatToParts(0).find((part) => part.type === "currency")?.value ?? code;
  const prefix = /^[A-Za-z]{2,}$/.test(symbol) ? `${symbol} ` : symbol;

  const iconSuffix = CURRENCY_ICON_SUFFIX[code];
  const coinSuffix = COIN_ICON_SUFFIX[code];
  const icon = iconSuffix ? `ti ti-currency-${iconSuffix}` : "ti ti-currency";
  const isDollar = Boolean(iconSuffix?.startsWith("dollar"));

  return {
    code,
    symbol,
    icon,
    coinIcon: code === FALLBACK_CURRENCY ? "ti ti-coin" : coinSuffix ? `ti ti-coin-${coinSuffix}` : icon,
    filterIcon: isDollar ? "ti ti-filter-dollar" : "ti ti-filter",
    compact: (value) => compactFormatter.format(finite(value)),
    amount: (value) => amountFormatter.format(finite(value)),
    whole: (value) => wholeFormatter.format(finite(value)),
    short: (value) => {
      const amount = finite(value);
      const abs = Math.abs(amount);
      const sign = amount < 0 ? "-" : "";
      if (abs >= 1e9) return `${sign}${prefix}${trimDecimals(abs / 1e9, 2)}B`;
      if (abs >= 1e6) return `${sign}${prefix}${trimDecimals(abs / 1e6, 2)}M`;
      if (abs >= 1e5) return `${sign}${prefix}${trimDecimals(abs / 1e3, 1)}K`;
      return `${sign}${prefix}${plainFormatter.format(abs)}`;
    },
  };
};

export const useMoneyFormat = (currencyCode?: string | null) =>
  useMemo(() => createMoneyFormat(currencyCode), [currencyCode]);
